import React, { lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Loader from '../../Components/Loader/Loader';

const AuthLayout = lazy(() => import('../../Layouts/AuthLayout/AuthLayout'));
const DashboardLayout = lazy(() => import('../DashboardLayout/DashboardLayout'));

const Login = lazy(() => import('../Pages/Auth/Login/LoginView'));
const ForgetPassword = lazy(() => import('../Pages/Auth/Login/ForgetPassword'));
const ResetPassword = lazy(() => import('../Pages/Auth/Login/ResetPassword'));

const DashboardView = lazy(() => import('../Pages/Dashboard/DashboardView'));
const Companies = lazy(() => import('../Pages/Companies/List'));
const Transactions = lazy(() => import('../Pages/Transactions/TransactionList'));
const InternalUsers = lazy(() => import('../Pages/InternalUsers/InternalUsersView'));
const CreateUser = lazy(() => import('../Pages/InternalUsers/CreateUser'));
const RolePermission = lazy(() => import('../Pages/RolePermissions/CreateRole'));
const EditRolePermission = lazy(() => import('../Pages/RolePermissions/EditRole'));
const RolePermissionList = lazy(() => import('../Pages/RolePermissions/ListRole'));
const Rules = lazy(() => import('../Pages/Rules/Rules'));
const AdminProfile = lazy(() => import('../Pages/AdminProfile/AdminProfile'));
const EditUser = lazy(() => import('../Pages/InternalUsers/EditUser'));
const SubscriptionList = lazy(() => import('../Pages/Subscription/SubscriptionList'));
const NewSubscriptions = lazy(() => import('../Pages/NewSubscriptions/NewSubscriptions'));
const Notifications = lazy(() => import('../Pages/Notifications/Notifications'));
const Success = lazy(() => import('../Pages/SuccessFailure/SubscriptionSuccess'));
const Failure = lazy(() => import('../Pages/SuccessFailure/SubscriptionFailure'));
const CompanyDetails = lazy(() => import('../Pages/CompanyDetails/CompanyDetails'));

const NotFoundView = lazy(() => import('../Pages/404/NotFoundPage'));
function RequireAuth({ isAuthenticated }) {
   let location = useLocation();

   if (!isAuthenticated) {
      return <Navigate to="/login" state={{ from: location }} />;
   }

   return (
      <Suspense fallback={<Loader />}>
         <DashboardLayout />
      </Suspense>
   );
}

function StrictlyNotRequireAuth({ isAuthenticated }) {
   let location = useLocation();

   if (isAuthenticated) {
      return <Navigate to="/dashboard" state={{ from: location }} />;
   }

   return (
      <Suspense fallback={<Loader />}>
         <AuthLayout />
      </Suspense>
   );
}

function RoutesComponent() {
   const { isAuthenticated } = useSelector((state) => state.superAdminAuth);
   // console.log(isAuthenticated);

   return (
      <HashRouter>
         <Routes>
            <Route element={<StrictlyNotRequireAuth isAuthenticated={isAuthenticated} />}>
               <Route
                  path="/login"
                  exact={true}
                  name="Login"
                  element={
                     <Suspense fallback={<Loader />}>
                        <Login />
                     </Suspense>
                  }
               />
               {/* <Route
                  path="/subscriptions"
                  exact={true}
                  name="Subscription"
                  element={
                     <Suspense fallback={<Loader/>}>
                        <SubscriptionList />
                     </Suspense>
                  }
               /> */}
               <Route
                  path="/forget-password"
                  exact={true}
                  name="ForgetPassword"
                  element={
                     <Suspense fallback={<Loader />}>
                        <ForgetPassword />
                     </Suspense>
                  }
               />
               <Route
                  path="/reset-password"
                  exact={true}
                  name="ResetPassword"
                  element={
                     <Suspense fallback={<Loader />}>
                        <ResetPassword />
                     </Suspense>
                  }
               />
            </Route>

            <Route element={<RequireAuth isAuthenticated={isAuthenticated} />}>
               <Route
                  path="/dashboard"
                  exact={true}
                  name="DashboardView"
                  element={
                     <Suspense fallback={<Loader />}>
                        <DashboardView />
                     </Suspense>
                  }
               />
               <Route
                  path="/companies"
                  exact={true}
                  name="Companies"
                  element={
                     <Suspense fallback={<Loader />}>
                        <Companies />
                     </Suspense>
                  }
               />
               <Route
                  path="/companies/company-details/:id"
                  exact={true}
                  name="Company Details"
                  element={
                     <Suspense fallback={<Loader />}>
                        <CompanyDetails />
                     </Suspense>
                  }
               />
               <Route
                  path="/users"
                  exact={true}
                  name="Users"
                  element={
                     <Suspense fallback={<Loader />}>
                        <InternalUsers />
                     </Suspense>
                  }
               />
               <Route
                  path="/users/create"
                  exact={true}
                  name="UserCreate"
                  element={
                     <Suspense fallback={<Loader />}>
                        <CreateUser />
                     </Suspense>
                  }
               />
               <Route
                  path="/users/:id/edit"
                  exact={true}
                  name="EditUser"
                  element={
                     <Suspense fallback={<Loader />}>
                        <EditUser />
                     </Suspense>
                  }
               />
               <Route
                  path="/create-roles"
                  exact={true}
                  name="CreateRoles"
                  element={
                     <Suspense fallback={<Loader />}>
                        <RolePermission />
                     </Suspense>
                  }
               />
               <Route
                  path="/edit-roles/:id"
                  exact={true}
                  name="EditRoles"
                  element={
                     <Suspense fallback={<Loader />}>
                        <EditRolePermission />
                     </Suspense>
                  }
               />
               <Route
                  path="/roles"
                  exact={true}
                  name="Roles"
                  element={
                     <Suspense fallback={<Loader />}>
                        <RolePermissionList />
                     </Suspense>
                  }
               />
               <Route
                  path="/rules"
                  exact={true}
                  name="Rules"
                  element={
                     <Suspense fallback={<Loader />}>
                        <Rules />
                     </Suspense>
                  }
               />
               <Route
                  path="/profile"
                  exact={true}
                  name="Profile"
                  element={
                     <Suspense fallback={<Loader />}>
                        <AdminProfile />
                     </Suspense>
                  }
               />
               <Route
                  path="/transactions"
                  exact={true}
                  name="Transactions"
                  element={
                     <Suspense fallback={<Loader />}>
                        <Transactions />
                     </Suspense>
                  }
               />
               <Route
                  path="/new-subscriptions"
                  exact={true}
                  name="NewSubscriptions"
                  element={
                     <Suspense fallback={<Loader />}>
                        <NewSubscriptions />
                     </Suspense>
                  }
               />
               <Route
                  path="/notifications"
                  exact={true}
                  name="Notifications"
                  element={
                     <Suspense fallback={<Loader />}>
                        <Notifications />
                     </Suspense>
                  }
               />
               <Route
                  path="/subscriptions"
                  exact={true}
                  name="Subscription"
                  element={
                     <Suspense fallback={<Loader />}>
                        <SubscriptionList />
                     </Suspense>
                  }
               />
            </Route>

            <Route
               path="*"
               exact={true}
               name="NotFoundPage"
               element={
                  <Suspense fallback={<Loader />}>
                     <NotFoundView />
                  </Suspense>
               }
            />
            <Route
               path="/subscriptions-failure"
               exact={true}
               name="Failure"
               element={
                  <Suspense fallback={<Loader />}>
                     <Failure />
                  </Suspense>
               }
            />
            <Route
               path="/subscriptions-success"
               exact={true}
               name="Success"
               element={
                  <Suspense fallback={<Loader />}>
                     <Success />
                  </Suspense>
               }
            />

            <Route path="" exact={true} name="RedirctToDashboard" element={<Navigate to="/dashboard" replace />} />
         </Routes>
      </HashRouter>
   );
}

export default RoutesComponent;
